import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Activity, BarChart3, Briefcase, Clock, Database, FileText, GitBranch,
  Network, RefreshCw, ShieldAlert, TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '../lib/api';
import {
  quantDashboard, quantListStrategies, quantGetStrategy,
  quantListAssets, quantGetAsset, quantListSessions, quantGetSession,
  quantListDataSources, quantGetDataSource,
  quantGetMasterRules, quantGetChangelog, quantKgSummary, getErrorMessage,
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import QuantMarkdown from '../components/QuantMarkdown';

// Fetch PNG come blob (apiClient già ha l'auth header)
const fetchFigureBlob = (filename) =>
  apiClient.get(`/admin/quant/figures/${filename}`, { responseType: 'blob' }).then(r => r.data);

const STATUS_COLORS = {
  baseline:                'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  no_edge:                 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  edge_ok_portfolio_issue: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200',
  promotable:              'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  paper_trading:           'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-200',
  deprecated:              'bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
};

const fmtPct = (v) => (v === null || v === undefined) ? 'n/d' : `${v > 0 ? '+' : ''}${v.toFixed(2)}%`;
const fmtNum = (v, d = 2) => (v === null || v === undefined) ? 'n/d' : v.toFixed(d);

// ============== DASHBOARD TAB ==============

const DashboardTab = ({ data, onOpen }) => {
  const kg = data?.kg;
  const status = data?.status;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Activity className="w-4 h-4 text-primary" /> Stato Vault
          </CardTitle>
        </CardHeader>
        <CardContent>
          {status?.available ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <KPI label="Versione" value={status.version || '0.2.0'} />
              <KPI label="Strategie" value={status.counts?.strategies || 0} />
              <KPI label="Asset" value={status.counts?.assets || 0} />
              <KPI label="Sessioni" value={status.counts?.sessions || 0} />
              <KPI label="Regimi taggati" value={kg?.by_node_type?.regime || 0} />
              <KPI label="KG nodi" value={kg?.n_nodes || 0} />
              <KPI label="KG archi" value={kg?.n_edges || 0} />
              <KPI label="Ultimo update" value={status.last_master_update || ''} />
            </div>
          ) : (
            <div className="text-sm text-red-500 flex items-center gap-2">
              <ShieldAlert className="w-4 h-4" /> Vault non accessibile
            </div>
          )}
        </CardContent>
      </Card>

      {kg?.top_strategies_per_regime && Object.keys(kg.top_strategies_per_regime).length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Network className="w-4 h-4 text-primary" /> Insight Knowledge Graph
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground mb-3">
              Sharpe condizionato per regime SPY · ordinato per impact
            </p>
            <div className="space-y-2">
              {Object.entries(kg.top_strategies_per_regime).map(([regime, entries]) => (
                <div key={regime} className="flex items-center gap-2 flex-wrap py-1.5 border-b last:border-0">
                  <Badge variant="outline" className="text-[10px] capitalize w-28 justify-center">
                    {regime}
                  </Badge>
                  <div className="flex flex-wrap gap-1.5 flex-1">
                    {entries.slice(0, 4).map((e, i) => (
                      <button
                        key={e.strategy}
                        onClick={() => onOpen('strategy', e.strategy)}
                        className={`text-xs px-2 py-0.5 rounded transition-colors ${
                          i === 0 ? 'bg-primary/15 text-primary font-medium' : 'bg-muted hover:bg-muted/70'
                        }`}
                      >
                        {e.strategy} <span className="font-mono">{e.sharpe_cond >= 0 ? '+' : ''}{e.sharpe_cond.toFixed(2)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {data?.main_index_body && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" /> Index del vault
            </CardTitle>
          </CardHeader>
          <CardContent>
            <QuantMarkdown
              figureUrlFor={fetchFigureBlob}
              onWikilinkClick={(target) => {
                const parts = target.split('/');
                if (parts.length === 2) {
                  const [section, slug] = parts;
                  const map = { strategies: 'strategy', assets: 'asset', sessions: 'session', 'data-sources': 'data-source' };
                  if (map[section]) onOpen(map[section], slug);
                }
              }}
            >
              {data.main_index_body}
            </QuantMarkdown>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const KPI = ({ label, value }) => (
  <div className="rounded-lg bg-muted/30 px-3 py-2">
    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="text-lg font-bold tabular-nums">{value || '—'}</p>
  </div>
);

// ============== STRATEGIES TAB ==============

const StrategiesTab = ({ onOpen }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    quantListStrategies()
      .then(r => setItems(r.data.items || []))
      .catch(e => toast.error(getErrorMessage(e, 'Errore caricamento strategie')))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="h-48 bg-muted animate-pulse rounded-xl" />;
  if (!items.length) return <Empty msg="Nessuna strategia trovata nel vault." />;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Briefcase className="w-4 h-4" /> Strategie testate
        </CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-muted-foreground border-b">
            <tr>
              <th className="px-2 py-2 text-left">Nome</th>
              <th className="px-2 py-2 text-left">Status</th>
              <th className="px-2 py-2 text-right">Sharpe pure</th>
              <th className="px-2 py-2 text-right">CAGR pure</th>
              <th className="px-2 py-2 text-right">Max DD pure</th>
              <th className="px-2 py-2 text-left">Last run</th>
            </tr>
          </thead>
          <tbody>
            {items.map(s => (
              <tr
                key={s.name}
                className="border-b cursor-pointer hover:bg-muted/40 transition-colors"
                onClick={() => onOpen('strategy', s.name)}
              >
                <td className="px-2 py-2 font-medium">{s.name}</td>
                <td className="px-2 py-2">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${STATUS_COLORS[s.status] || 'bg-muted'}`}>
                    {s.status}
                  </span>
                </td>
                <td className="px-2 py-2 text-right tabular-nums">{fmtNum(s.metrics?.sharpe_pure)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmtPct(s.metrics?.cagr_pure)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmtPct(s.metrics?.max_dd_pure)}</td>
                <td className="px-2 py-2 text-xs text-muted-foreground">{s.last_run || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
};

// ============== ASSETS TAB ==============

const AssetsTab = ({ onOpen }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    quantListAssets()
      .then(r => setItems(r.data.items || []))
      .catch(e => toast.error(getErrorMessage(e, 'Errore caricamento asset')))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="h-48 bg-muted animate-pulse rounded-xl" />;
  if (!items.length) return <Empty msg="Nessun asset analizzato. Lancia analyze_asset.py in Quant Lab." />;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {items.map(a => (
        <Card key={a.symbol} className="cursor-pointer hover:border-primary/40 transition-colors"
              onClick={() => onOpen('asset', a.symbol)}>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <p className="font-bold">{a.symbol}</p>
                <p className="text-xs text-muted-foreground truncate">{a.name}</p>
              </div>
              <Badge variant="outline" className="text-[10px] capitalize">{a.asset_class}</Badge>
            </div>
            <div className="mt-2 flex items-center gap-3 text-xs">
              <span className="text-muted-foreground">trend:</span>
              <span className={`font-medium ${a.trend === 'uptrend' ? 'text-emerald-600' : a.trend === 'downtrend' ? 'text-red-500' : ''}`}>
                {a.trend}
              </span>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">aggiornato: {a.last_data || 'n/d'}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ============== SESSIONS TAB ==============

const SessionsTab = ({ onOpen }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    quantListSessions()
      .then(r => setItems(r.data.items || []))
      .catch(e => toast.error(getErrorMessage(e, 'Errore caricamento sessioni')))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="h-48 bg-muted animate-pulse rounded-xl" />;
  if (!items.length) return <Empty msg="Nessuna sessione registrata." />;

  return (
    <Card>
      <CardContent className="pt-4 space-y-2">
        {items.map(s => (
          <div key={s.slug}
               onClick={() => onOpen('session', s.slug)}
               className="flex items-center gap-3 p-2 rounded hover:bg-muted/40 cursor-pointer transition-colors">
            <Clock className="w-4 h-4 text-muted-foreground shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{s.title}</p>
              <p className="text-xs text-muted-foreground">{s.date} · phase {s.phase || '—'}</p>
            </div>
            {s.status && <Badge variant="outline" className="text-[10px]">{s.status}</Badge>}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

// ============== RULES TAB ==============

const RulesTab = ({ onOpen }) => {
  const [rules, setRules] = useState(null);
  const [changelog, setChangelog] = useState(null);
  const [view, setView] = useState('master');
  useEffect(() => {
    quantGetMasterRules().then(r => setRules(r.data)).catch(() => {});
    quantGetChangelog().then(r => setChangelog(r.data)).catch(() => {});
  }, []);

  const current = view === 'master' ? rules : changelog;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Button size="sm" variant={view === 'master' ? 'default' : 'outline'} onClick={() => setView('master')}>
          Master rules
        </Button>
        <Button size="sm" variant={view === 'changelog' ? 'default' : 'outline'} onClick={() => setView('changelog')}>
          Changelog
        </Button>
        {current?.frontmatter?.version && (
          <Badge variant="outline" className="ml-auto">v{current.frontmatter.version}</Badge>
        )}
      </div>
      <Card>
        <CardContent className="pt-4">
          {current ? (
            <QuantMarkdown figureUrlFor={fetchFigureBlob} onWikilinkClick={() => {}}>
              {current.body || ''}
            </QuantMarkdown>
          ) : (
            <div className="h-32 bg-muted animate-pulse rounded" />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============== DETAIL VIEW ==============

const DetailView = ({ kind, id, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const fetcher = kind === 'strategy' ? quantGetStrategy
                  : kind === 'asset' ? quantGetAsset
                  : kind === 'session' ? quantGetSession
                  : kind === 'data-source' ? quantGetDataSource
                  : null;
    if (!fetcher) { setLoading(false); return; }
    fetcher(id)
      .then(r => setData(r.data))
      .catch(e => toast.error(getErrorMessage(e, 'Errore caricamento')))
      .finally(() => setLoading(false));
  }, [kind, id]);

  const handleWikilink = useCallback((target) => {
    const [section, slug] = target.split('/');
    const map = { strategies: 'strategy', assets: 'asset', sessions: 'session', 'data-sources': 'data-source' };
    if (map[section] && slug) onClose(map[section], slug);
  }, [onClose]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            {kindIcon(kind)} {capitalize(kind)} · {id}
          </CardTitle>
          <Button size="sm" variant="outline" onClick={() => onClose(null, null)}>
            ← Torna alla lista
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-64 bg-muted animate-pulse rounded" />
        ) : data ? (
          <QuantMarkdown figureUrlFor={fetchFigureBlob} onWikilinkClick={handleWikilink}>
            {data.body || ''}
          </QuantMarkdown>
        ) : (
          <Empty msg="Contenuto non trovato." />
        )}
      </CardContent>
    </Card>
  );
};

const kindIcon = (kind) => {
  const Icon = kind === 'strategy' ? Briefcase
             : kind === 'asset' ? TrendingUp
             : kind === 'session' ? Clock
             : kind === 'data-source' ? Database
             : FileText;
  return <Icon className="w-4 h-4" />;
};
const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

const Empty = ({ msg }) => (
  <Card className="border-dashed">
    <CardContent className="pt-12 pb-12 text-center text-sm text-muted-foreground">
      {msg}
    </CardContent>
  </Card>
);

// ============== ROOT ==============

const QuantHubPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = searchParams.get('tab') || 'dashboard';
  const detailKind = searchParams.get('kind');
  const detailId = searchParams.get('id');

  const [dashData, setDashData] = useState(null);
  const [loadingDash, setLoadingDash] = useState(true);

  const refreshDashboard = useCallback(() => {
    setLoadingDash(true);
    quantDashboard()
      .then(r => setDashData(r.data))
      .catch(e => {
        if (e.response?.status === 503) {
          toast.error('Quant Lab vault non montato nel container. Verifica docker-compose.');
        } else {
          toast.error(getErrorMessage(e, 'Errore caricamento dashboard'));
        }
      })
      .finally(() => setLoadingDash(false));
  }, []);

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/'); return; }
    refreshDashboard();
  }, [user, navigate, refreshDashboard]);

  const openDetail = useCallback((kind, id) => {
    if (!kind || !id) {
      setSearchParams({ tab });
    } else {
      setSearchParams({ tab, kind, id });
    }
  }, [setSearchParams, tab]);

  const setTab = (newTab) => {
    setSearchParams({ tab: newTab });
  };

  return (
    <div className="space-y-5 animate-in" data-testid="quant-hub-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <GitBranch className="w-7 h-7 text-primary" strokeWidth={1.5} />
            Quant Lab
          </h1>
          <p className="text-muted-foreground">
            Vault locale read-only · {dashData?.status?.root || '/quant-lab'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={refreshDashboard} disabled={loadingDash}>
            <RefreshCw className={`w-4 h-4 ${loadingDash ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {detailKind && detailId ? (
        <DetailView kind={detailKind} id={detailId} onClose={openDetail} />
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-5 w-full md:w-fit">
            <TabsTrigger value="dashboard"><BarChart3 className="w-4 h-4 mr-1" />Dashboard</TabsTrigger>
            <TabsTrigger value="strategies"><Briefcase className="w-4 h-4 mr-1" />Strategie</TabsTrigger>
            <TabsTrigger value="assets"><TrendingUp className="w-4 h-4 mr-1" />Asset</TabsTrigger>
            <TabsTrigger value="sessions"><Clock className="w-4 h-4 mr-1" />Sessioni</TabsTrigger>
            <TabsTrigger value="rules"><FileText className="w-4 h-4 mr-1" />Rules</TabsTrigger>
          </TabsList>
          <TabsContent value="dashboard" className="mt-4">
            {loadingDash
              ? <div className="h-48 bg-muted animate-pulse rounded-xl" />
              : <DashboardTab data={dashData} onOpen={openDetail} />}
          </TabsContent>
          <TabsContent value="strategies" className="mt-4">
            <StrategiesTab onOpen={openDetail} />
          </TabsContent>
          <TabsContent value="assets" className="mt-4">
            <AssetsTab onOpen={openDetail} />
          </TabsContent>
          <TabsContent value="sessions" className="mt-4">
            <SessionsTab onOpen={openDetail} />
          </TabsContent>
          <TabsContent value="rules" className="mt-4">
            <RulesTab onOpen={openDetail} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default QuantHubPage;
